import React, { useEffect } from 'react';
import { Outlet, ScrollRestoration } from 'react-router-dom';
import { AppProvider } from './AppProvider';
import { ChatViewProvider } from './shared/use-chat-view';
import { amplitudeTracker } from '../lib/shared/amplitude-tracker';
import { setCustomHeightVariable } from '../lib/shared/dimension-helpers';
import { pagePathnames } from './pages/page-routes';
import { logEvent } from '../lib/shared/event-logger';
import { EVENT_TYPES } from '../lib/shared/event-types';
import { useRouting } from './shared/use-routing';
import { i18n } from '../i18n/i18n';
import config from '../config';

amplitudeTracker.init(config.amplitudeApiKey, config.amplitudeServerUrl);
amplitudeTracker.onSubscribe();

function setMetaTagContent(selector, content) {
    document.querySelector(selector)?.setAttribute('content', content);
}

function App() {
    const { location, lang, stripLangPrefix } = useRouting();

    useEffect(() => {
        setCustomHeightVariable();
        window.addEventListener('resize', setCustomHeightVariable);
    }, []);

    useEffect(() => {
        if (i18n.language !== lang) {
            i18n.changeLanguage(lang);
        }

        document.documentElement.lang = lang;
    }, [lang]);

    useEffect(() => {
        document.title = 'Boundly';
        setMetaTagContent('meta[name="description"]', 'Boundly turns conversations about your life into a beautiful, private ebook for your family — voice & text recording, adaptive questions, zero tech skills needed.');
        setMetaTagContent('meta[property="og:title"]', 'Boundly: Preserve your life story, one conversation at a time.');
        setMetaTagContent('meta[property="og:description"]', 'Chat about your life and watch your memories grow into chapters — childhood, family, and beyond. Turn them into a beautiful, private ebook with Boundly.');
        setMetaTagContent('meta[name="twitter:title"]', 'Boundly: Preserve your life story, one conversation at a time.');
        setMetaTagContent('meta[property="twitter:description"]', 'Chat about your life and watch your memories grow into chapters — childhood, family, and beyond. Turn them into a beautiful, private ebook with Boundly.');
    }, [lang]);

    useEffect(() => {
        const pathname = stripLangPrefix(location.pathname);

        if (pathname === pagePathnames.aboutUs) {
            logEvent(EVENT_TYPES.ABOUT_US_PAGE_VISITED);
        }

        if (pathname === pagePathnames.dashboard) {
            logEvent(EVENT_TYPES.DASHBOARD_PAGE_VISITED);
        }

        if (pathname === pagePathnames.premium) {
            logEvent(EVENT_TYPES.PREMIUM_PAGE_VISITED);
        }

        if (pathname === pagePathnames.signIn) {
            logEvent(EVENT_TYPES.SIGN_IN_PAGE_VISITED);
        }

        if (pathname === pagePathnames.start) {
            logEvent(EVENT_TYPES.HOMEPAGE_VISITED);
        }

        if (pathname === pagePathnames.contactUs || pathname === pagePathnames.impressum) {
            logEvent(EVENT_TYPES.CONTACT_US_PAGE_VISITED);
        }
    }, [location.pathname, stripLangPrefix]);

    return (
        <>
            <ScrollRestoration />
            <AppProvider>
                <ChatViewProvider>
                    <Outlet />
                </ChatViewProvider>
            </AppProvider>
        </>
    );
}

export {
    App
};
